import {
  ActionList,
  ActionMenu,
  Button,
  Heading,
  IconButton,
  Label,
  Link,
  Pagination,
  RelativeTime,
  Spinner,
  VisuallyHidden,
} from '@primer/react'
import {AriaAlert, AriaStatus, type Column, DataTable, Dialog, Table} from '@primer/react/experimental'
import {KebabHorizontalIcon, ScreenFullIcon, ScreenNormalIcon} from '@primer/octicons-react'
import {DEFAULT_SPACE_COLOR, SPACE_COLORS} from '@github-ui/network-graph-element/space-colors'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {clsx} from 'clsx'
import {useCallback, useId, useRef, useState} from 'react'

import type {AheadBehind, NetworkGraphTableRow} from './types'
import {useNetworkTableData} from './use-network-table-data'
import styles from './NetworkGraphTable.module.css'

export interface NetworkGraphTableProps {
  /** Rows to render directly. Used by stories, tests, and SSR; ignored when `tableDataUrl` is set. */
  rows?: NetworkGraphTableRow[]
  /**
   * URL of the `network/table` endpoint. When provided, the table lazily fetches the real network
   * graph data the first time the dialog is opened (polling while the graph builds) instead of
   * rendering the `rows` prop.
   */
  tableDataUrl?: string
}

const SHORT_SHA_LENGTH = 7

const EMPTY_ROWS: NetworkGraphTableRow[] = []

/**
 * Deterministic stand-in that maps a branch name to a lane color from the shared chart palette.
 * In the real feature this must key off the same chart lane (`space`) each row belongs to so the
 * colors genuinely match the graph; the dummy prototype rows don't carry a lane, so the name is
 * hashed here.
 */
function branchLaneColor(branch: string): string {
  let hash = 0
  for (let i = 0; i < branch.length; i++) {
    hash = (hash + branch.charCodeAt(i)) % SPACE_COLORS.length
  }
  return SPACE_COLORS[hash] ?? DEFAULT_SPACE_COLOR
}

/**
 * Decorative color indicator that ties a table row to its lane in the chart. The color is
 * supplementary to the branch name text (WCAG 1.4.1), so the dot is hidden from assistive
 * tech; a token border guarantees a visible boundary for pale palette colors (WCAG 1.4.11).
 */
function BranchColorDot({color}: {color: string}) {
  return <span aria-hidden="true" className={styles.branchDot} style={{backgroundColor: color}} />
}

const PLACEHOLDER = '\u2014'

/**
 * Consistent placeholder for a missing value. The dash is hidden from assistive tech in favor of
 * an explicit phrase so screen reader users hear why the cell is empty rather than a bare
 * punctuation character.
 */
function EmptyCell({label = 'Not available'}: {label?: string}) {
  return (
    <>
      <span aria-hidden="true">{PLACEHOLDER}</span>
      <VisuallyHidden>{label}</VisuallyHidden>
    </>
  )
}

/** Renders a plain text value, falling back to the shared placeholder when it is absent. */
function TextCell({value}: {value: string | null}) {
  return value ? <>{value}</> : <EmptyCell />
}

function BranchCell({branch}: {branch: string | null}) {
  if (!branch) {
    return <EmptyCell />
  }
  return (
    <span className={styles.branchCell}>
      <BranchColorDot color={branchLaneColor(branch)} />
      <span>{branch}</span>
    </span>
  )
}

/**
 * The avatar is purely decorative next to that text (`alt=""`), which keeps the cell's accessible
 * name a single readable identity rather than a doubled one. It loads lazily because the dialog
 * renders two avatars per row for every row in the graph, and the rows below the fold should not
 * be requesting and decoding images on the dialog-open critical path.
 */
function AuthorCell({name, login, avatarUrl}: {name: string | null; login: string | null; avatarUrl: string | null}) {
  // `||` rather than `??` throughout: git lets a commit carry an empty author name, and an empty
  // string must fall through to the login instead of rendering "() " or collapsing the cell.
  const label = name && login ? `${name} (@${login})` : name || login
  if (!label) {
    return <EmptyCell />
  }
  return (
    <span className={styles.authorCell}>
      {avatarUrl && <GitHubAvatar src={avatarUrl} size={16} alt="" loading="lazy" />}
      <span>{label}</span>
    </span>
  )
}

/** Compact, scannable rendering of an `[ahead, behind]` pair, e.g. "12 ahead, 3 behind". */
function formatAheadBehind([ahead, behind]: AheadBehind): string {
  if (ahead > 0 && behind > 0) {
    return `${ahead} ahead, ${behind} behind`
  }
  if (ahead > 0) {
    return `${ahead} ahead`
  }
  if (behind > 0) {
    return `${behind} behind`
  }
  return 'Up to date'
}

function ComparisonValue({
  count,
  direction,
  path,
  descriptionId,
}: {
  count: number
  direction: 'ahead' | 'behind'
  path: string | null
  descriptionId: string | undefined
}) {
  const text = `${count} ${direction}`
  if (!path) {
    return <span>{text}</span>
  }
  return (
    <Link href={path} aria-describedby={descriptionId}>
      {text}
    </Link>
  )
}

/**
 * Shows how far the branch has diverged from the viewed repository's default branch. The visible
 * text is the compact pair so the column stays scannable, while assistive tech announces the
 * endpoint's fuller sentence, which also names the base branch ("2 commits ahead of main"). Git
 * cannot compare every head, and those rows arrive without a comparison rather than failing the
 * whole table, so they keep the column and render an explicit placeholder.
 *
 * The default branch is the baseline the other rows are measured against, so it is labelled rather
 * than compared against itself — matching the repository branches page. The label is real text, so
 * the cell announces "Default" instead of a placeholder phrase for an absent comparison.
 */
function AheadBehindCell({
  isDefaultBranch,
  aheadBehind,
  description,
  aheadPath,
  behindPath,
}: {
  isDefaultBranch: boolean
  aheadBehind: AheadBehind | null
  description: string | null
  aheadPath: string | null
  behindPath: string | null
}) {
  const descriptionId = useId()
  if (isDefaultBranch) {
    return <Label>Default</Label>
  }
  if (!aheadBehind) {
    return <EmptyCell label="Comparison not available" />
  }
  const summary = formatAheadBehind(aheadBehind)
  const [ahead, behind] = aheadBehind
  const hasLink = (ahead > 0 && aheadPath !== null) || (behind > 0 && behindPath !== null)
  if (!hasLink && !description) {
    return <span>{summary}</span>
  }
  if (!hasLink) {
    return (
      <span>
        <span aria-hidden="true">{summary}</span>
        <VisuallyHidden>{description}</VisuallyHidden>
      </span>
    )
  }
  return (
    <span>
      {ahead > 0 && (
        <ComparisonValue
          count={ahead}
          direction="ahead"
          path={aheadPath}
          descriptionId={description ? descriptionId : undefined}
        />
      )}
      {ahead > 0 && behind > 0 && <span>, </span>}
      {behind > 0 && (
        <ComparisonValue
          count={behind}
          direction="behind"
          path={behindPath}
          descriptionId={description ? descriptionId : undefined}
        />
      )}
      {description && (
        <VisuallyHidden id={descriptionId} aria-hidden="true">
          {description}
        </VisuallyHidden>
      )}
    </span>
  )
}

/**
 * Renders a compact 7-char commit SHA while keeping the full hash available to assistive tech
 * (visually hidden). Shortening the visible text keeps the table scannable without hiding
 * information or requiring a mouse-only horizontal scroll to read the rest.
 */
function CommitShaCell({sha}: {sha: string | null}) {
  if (!sha) {
    return <EmptyCell />
  }
  const short = sha.slice(0, SHORT_SHA_LENGTH)
  if (short === sha) {
    return <span className={styles.shaCell}>{sha}</span>
  }
  return (
    <span className={styles.shaCell}>
      <span aria-hidden="true">{short}</span>
      <VisuallyHidden>{sha}</VisuallyHidden>
    </span>
  )
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/**
 * `<relative-time>` parses a bare `YYYY-MM-DD` as UTC midnight, which can render the previous
 * day in negative-offset timezones. Anchor a date-only value to local midnight so the calendar
 * date stays stable; pass full timestamps through unchanged.
 */
export function toStableDateTimeAttr(value: string): string {
  return DATE_ONLY.test(value) ? `${value}T00:00:00` : value
}

function DateCell({value}: {value: string | null}) {
  if (!value) {
    return <EmptyCell />
  }
  // Render an absolute date with no hover title: title tooltips are inaccessible to keyboard and
  // touch, and for date-only values the anchored midnight would expose fabricated commit-time data.
  return (
    <RelativeTime
      datetime={toStableDateTimeAttr(value)}
      format="datetime"
      day="numeric"
      month="short"
      year="numeric"
      noTitle
    />
  )
}

// Columns are grouped commit-then-source, and each group ends with its commit SHA. The SHA is
// unique per row, so it is the row header: navigating cells gives screen readers a distinguishing
// row context (unlike Repo, which repeats across commits).
const columns: Array<Column<NetworkGraphTableRow>> = [
  {header: 'Repo', field: 'repo', renderCell: row => row.repo},
  {header: 'Branch', field: 'branch', renderCell: row => <BranchCell branch={row.branch} />},
  {
    header: 'Ahead / behind',
    field: 'aheadBehind',
    renderCell: row => (
      <AheadBehindCell
        isDefaultBranch={row.isDefaultBranch}
        aheadBehind={row.aheadBehind}
        description={row.aheadBehindDescription}
        aheadPath={row.aheadPath}
        behindPath={row.behindPath}
      />
    ),
  },
  {header: 'Commit Date', field: 'commitDate', renderCell: row => <DateCell value={row.commitDate} />},
  {
    header: 'Author',
    field: 'commitAuthor',
    renderCell: row => (
      <AuthorCell name={row.commitAuthor} login={row.commitAuthorLogin} avatarUrl={row.commitAuthorAvatarUrl} />
    ),
  },
  {
    header: 'Commit Description',
    field: 'commitDescription',
    renderCell: row => <TextCell value={row.commitDescription} />,
  },
  {header: 'Commit', field: 'commit', rowHeader: true, renderCell: row => <CommitShaCell sha={row.commit} />},
  {header: 'Compare Repo', field: 'sourceRepo', renderCell: row => <TextCell value={row.sourceRepo} />},
  {header: 'Compare Branch', field: 'sourceBranch', renderCell: row => <BranchCell branch={row.sourceBranch} />},
  {header: 'Compare Date', field: 'sourceCommitDate', renderCell: row => <DateCell value={row.sourceCommitDate} />},
  {
    header: 'Compare Author',
    field: 'sourceCommitAuthor',
    renderCell: row => (
      <AuthorCell
        name={row.sourceCommitAuthor}
        login={row.sourceCommitAuthorLogin}
        avatarUrl={row.sourceCommitAuthorAvatarUrl}
      />
    ),
  },
  {
    header: 'Compare Commit Description',
    field: 'sourceCommitDescription',
    renderCell: row => <TextCell value={row.sourceCommitDescription} />,
  },
  {header: 'Compare Commit', field: 'sourceCommit', renderCell: row => <CommitShaCell sha={row.sourceCommit} />},
]

/**
 * Accessible "View as table" affordance for the network graph. A "Chart options" kebab menu
 * opens a dialog containing a Primer DataTable representation of the graph data. The kebab is
 * described as offering the alternate table view, and the table lives in a keyboard-operable,
 * labelled scroll region that can be maximized to fill the viewport.
 */
export function NetworkGraphTable({rows = EMPTY_ROWS, tableDataUrl}: NetworkGraphTableProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [maximized, setMaximized] = useState(false)
  // Latches true the first time the dialog opens so the (potentially large) payload is fetched
  // lazily and only once, then cached by the hook for subsequent opens.
  const [hasRequestedData, setHasRequestedData] = useState(false)
  // Bumped to retry the fetch after an error.
  const [reloadToken, setReloadToken] = useState(0)
  const tableTitleId = useId()
  const menuHintId = useId()
  // Captures the kebab so the dialog returns focus to it on close (SC 2.4.3). Passing our own
  // ref to ActionMenu (rather than the IconButton) avoids ActionMenu.Anchor overriding it.
  const anchorRef = useRef<HTMLElement>(null)

  const {
    status,
    rows: resolvedRows,
    page,
    pageCount,
    goToPage,
  } = useNetworkTableData(tableDataUrl, rows, hasRequestedData, reloadToken)
  const loadedAnnouncement = `${resolvedRows.length} ${resolvedRows.length === 1 ? 'commit' : 'commits'} loaded.`

  const isLoading = status === 'idle' || status === 'loading'
  const isLoadingInitial = isLoading && resolvedRows.length === 0
  const isError = status === 'error'
  const showsTable = !isLoadingInitial && !isError && resolvedRows.length > 0

  const openDialog = useCallback(() => {
    setDialogOpen(true)
    setHasRequestedData(true)
  }, [])
  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setMaximized(false)
  }, [])
  const toggleMaximized = useCallback(() => setMaximized(current => !current), [])
  const retry = useCallback(() => setReloadToken(token => token + 1), [])

  /*
   * Maximize/restore is an action button (like a media player's fullscreen control): the icon and
   * accessible name both describe the next action, so no aria-pressed.
   */
  const sizeToggle = (
    <IconButton
      aria-label={maximized ? 'Restore dialog size' : 'Maximize dialog'}
      icon={maximized ? ScreenNormalIcon : ScreenFullIcon}
      variant="invisible"
      size="small"
      onClick={toggleMaximized}
    />
  )

  return (
    <>
      <ActionMenu anchorRef={anchorRef}>
        <ActionMenu.Anchor>
          <IconButton
            aria-label="Chart options"
            aria-describedby={menuHintId}
            icon={KebabHorizontalIcon}
            variant="invisible"
            size="small"
          />
        </ActionMenu.Anchor>
        {/*
         * The kebab is right-aligned in the page header, so the menu grows leftward from it
         * (its right edge stays on the anchor) instead of overflowing toward the page edge.
         */}
        <ActionMenu.Overlay align="end" width="auto">
          <ActionList>
            <ActionList.Item onSelect={openDialog}>View graph data as a table</ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <VisuallyHidden id={menuHintId} aria-hidden="true">
        The network graph is also available as a data table.
      </VisuallyHidden>
      {dialogOpen && (
        <Dialog
          onClose={closeDialog}
          returnFocusRef={anchorRef}
          position={maximized ? {narrow: 'fullscreen', regular: 'center'} : 'center'}
          width={maximized ? '100dvw' : 'min(1200px, 90vw)'}
          className={clsx(styles.dialog, maximized && styles.maximizedDialog)}
          renderHeader={({dialogLabelId}) => (
            <Dialog.Header id={dialogLabelId} className="px-3 py-2 gap-2 d-flex flex-row flex-items-center">
              <Heading as="h1" variant="small">
                Network graph
              </Heading>
              {!isLoadingInitial && isLoading && <Spinner size="small" />}
            </Dialog.Header>
          )}
        >
          <Dialog.Body className={styles.dialogBody}>
            {!isLoading && !isError && (
              <VisuallyHidden>
                <AriaStatus announceOnShow>{loadedAnnouncement}</AriaStatus>
              </VisuallyHidden>
            )}
            {/*
             * When the table renders, the size toggle moves into Table.Actions so it sits inline
             * with the "Commits" heading. The status panes have no heading to sit beside, so there
             * it keeps its own right-aligned row.
             */}
            {!showsTable && <div className={styles.toolbar}>{sizeToggle}</div>}
            {isLoadingInitial ? (
              <div className={styles.statusPane}>
                <Spinner aria-label="Loading network graph data" />
                <VisuallyHidden>
                  <AriaStatus announceOnShow delayMs={500}>
                    Loading network graph data.
                  </AriaStatus>
                </VisuallyHidden>
              </div>
            ) : isError ? (
              <div className={styles.statusPane}>
                <AriaAlert as="p" className={styles.emptyState}>
                  Couldn’t load the network graph data.
                </AriaAlert>
                <Button onClick={retry}>Try again</Button>
              </div>
            ) : resolvedRows.length === 0 ? (
              <AriaStatus announceOnShow as="p" className={styles.emptyState}>
                No commits to display.
              </AriaStatus>
            ) : (
              // Table.Container renders a Primer ScrollableRegion that becomes a labelled,
              // keyboard-focusable region (role="region" + tabIndex) whenever the wide table
              // overflows, so keyboard users can scroll it (WCAG 2.1.1) without a custom wrapper.
              // Cells are kept to a single line (see styles.singleLineTable) so rows stay scannable
              // and the table scrolls horizontally rather than wrapping to variable-height rows.
              // Its grid also lays Table.Title and Table.Actions out on a single row.
              <>
                <Table.Container className={styles.singleLineTable}>
                  <Table.Title as="h2" id={tableTitleId}>
                    Commits
                  </Table.Title>
                  <Table.Actions>{sizeToggle}</Table.Actions>
                  <DataTable aria-labelledby={tableTitleId} data={resolvedRows} columns={columns} />
                </Table.Container>
                {pageCount > 1 && (
                  <Pagination
                    pageCount={pageCount}
                    currentPage={page}
                    onPageChange={(event, newPage) => {
                      event.preventDefault()
                      if (status !== 'loading') goToPage(newPage)
                    }}
                  />
                )}
              </>
            )}
          </Dialog.Body>
        </Dialog>
      )}
    </>
  )
}
